import { useRef } from "react";
import Figure1 from "./Figure1";
import { fontSizes } from "../typography";
import { StickyContainer } from "../SharedUI";
import { useContainerDimensions } from "../../hooks/useContainerDimensions";
import styled from "@emotion/styled";

const ReportingProblemContainer = styled(StickyContainer)`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const AnnotationText = styled.p`
  font-size: ${fontSizes.reportingProblemCaption};
  padding: 0 0 2rem 0;
  margin: 0;
  width: 80%;
  text-align: center;
  line-height: 1.15;
  color: #8e8e8e;
`;

interface VisualizationProps {
  currentStep: string;
}

// modified version of figure 1 palette for better font readability
const palette = ["#efefef", "#2d8f5cff", "#67bc81ff", "#b9bc1bff", "#45c2efff"];

export default function ReportingProblemViz({
  currentStep,
}: VisualizationProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dimensions = useContainerDimensions(containerRef);

  const svgWidth = dimensions.width;

  const margin =
    svgWidth >= 900
      ? { top: 90, right: 50, bottom: 60, left: 90 }
      : { top: 60, right: 47, bottom: 50, left: 50 };

  // Calculate square side based on available width
  const availableWidth = svgWidth - margin.left - margin.right;
  const squareSide = availableWidth;

  // Set SVG height to accommodate the square grid + margins
  const svgHeight = squareSide + margin.top + margin.bottom;

  const offsetX = margin.left;
  const offsetY = margin.top;

  const xLabel = "Importer Countries Reporting";
  const yLabel = "Exporter Countries Reporting";

  function getXLabelColor(currentStep: string) {
    switch (currentStep) {
      case "ideal":
        return palette[1];
      case "reality-both-reported-aligned":
        return palette[1];
      case "reality-add-both-misaligned":
        return palette[2];
      case "reality-add-importer-only":
        return palette[3];
      case "reality-add-exporter-only":
        return "gray";
      default:
        return "gray";
    }
  }

  function getYLabelColor(currentStep: string) {
    switch (currentStep) {
      case "ideal":
        return palette[1];
      case "reality-both-reported-aligned":
        return palette[1];
      case "reality-add-both-misaligned":
        return palette[2];
      case "reality-add-importer-only":
        return "gray";
      case "reality-add-exporter-only":
        return palette[4];
      default:
        return "gray";
    }
  }

  return (
    <ReportingProblemContainer ref={containerRef}>
      <svg
        width={svgWidth}
        height={svgHeight}
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      >
        {/* Title */}
        <text
          x={svgWidth / 2}
          y={offsetY - 20}
          fontSize={fontSizes.reportingProblemTitle}
          fill="rgb(77,77,77)"
          textAnchor="middle"
          style={{ textTransform: "uppercase", fontWeight: 600 }}
        >
          {currentStep === "ideal"
            ? "Trade Data Reporting in an Ideal Scenario"
            : "Trade Data Reporting in Reality (2010)"}
        </text>
        <Figure1
          currentStep={currentStep}
          innerWidth={squareSide}
          innerHeight={squareSide}
          margins={margin}
        />
        {/* Axes group */}
        <g transform={`translate(${offsetX}, ${offsetY})`}>
          <line
            x1={0}
            y1={squareSide}
            x2={squareSide}
            y2={squareSide}
            stroke="#777"
          />

          <line x1={0} y1={0} x2={0} y2={squareSide} stroke="#777" />

          <text
            x={squareSide / 2}
            y={squareSide + 30}
            textAnchor="middle"
            fontSize={fontSizes.reportingProblemAxisLabels}
            fill={getXLabelColor(currentStep)}
            fontWeight={
              currentStep !== "reality" &&
              currentStep !== "reality-add-blue" &&
              currentStep !== ""
                ? "bold"
                : "normal"
            }
          >
            {xLabel}
          </text>

          <text
            transform={`translate(${-Math.max(16, margin.left - 80)}, ${
              squareSide / 2
            }) rotate(-90)`}
            textAnchor="middle"
            fontSize={fontSizes.reportingProblemAxisLabels}
            fill={getYLabelColor(currentStep)}
            fontWeight={
              currentStep !== "reality" &&
              currentStep !== "reality-add-orange" &&
              currentStep !== ""
                ? "bold"
                : "normal"
            }
          >
            {yLabel}
          </text>
        </g>
      </svg>
      <AnnotationText>
        Countries appear in the rows as exporters and in the columns as
        importers. Each square therefore represents trade between a specific
        exporter-importer pair. Countries are ordered by total trade volume, so
        the biggest traders appear toward the top-right of the chart. The
        diagonal is blank because it represents countries trading with
        themselves, which is excluded intentionally.
      </AnnotationText>
    </ReportingProblemContainer>
  );
}
